/**
 * Укладка плиток библиотеки колонками: элемент i попадает в колонку i % n,
 * внутри колонки карточки стоят вплотную. Порядок в массиве — порядок чтения
 * слева направо, его же держит DOM, чтобы стрелки влево/вправо не сбивались.
 *
 * Высота повторяет карточку LibraryItemTile: аспект на превью, подпись h-7, рамка 1px.
 * Пропорцию по файлу не измеряем — её задаёт класс, как и раньше.
 */

/** Минимум колонки — тот же minmax(8.5rem), что у прежней сетки auto-fill. */
export const LIBRARY_MASONRY_MIN_COLUMN_REM = 8.5

/** gap-3 и p-3. */
export const LIBRARY_MASONRY_SPACE_REM = 0.75

/** Строка подписи плитки: h-7. */
export const LIBRARY_TILE_CAPTION_REM = 1.75

/** Рамка карточки по одной оси: 1px сверху и 1px снизу (и так же слева и справа). */
export const LIBRARY_TILE_BORDER_PX = 2

const DEFAULT_ASPECT_RATIO = 16 / 9

/** width/height для классов плитки. Незнакомый класс — как aspect-video. */
export function libraryAspectRatio(aspectClass: string): number {
  const arbitrary = aspectClass.match(/aspect-\[(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)\]/)
  if (arbitrary) {
    const width = Number(arbitrary[1])
    const height = Number(arbitrary[2])
    if (width > 0 && height > 0) return width / height
  }
  if (aspectClass.includes("aspect-square")) return 1
  if (aspectClass.includes("aspect-video")) return DEFAULT_ASPECT_RATIO
  return DEFAULT_ASPECT_RATIO
}

/** Высота карточки при заданной ширине колонки (border-box, как у самой плитки). */
export function libraryTileHeight(columnWidth: number, widthOverHeight: number, captionPx = 28): number {
  const ratio = Number.isFinite(widthOverHeight) && widthOverHeight > 0 ? widthOverHeight : DEFAULT_ASPECT_RATIO
  const contentWidth = Math.max(0, columnWidth - LIBRARY_TILE_BORDER_PX)
  return contentWidth / ratio + captionPx + LIBRARY_TILE_BORDER_PX
}

export type LibraryMasonryPlacement = {
  index: number
  column: number
  top: number
  left: number
  width: number
  height: number
}

export type LibraryMasonryLayout = {
  columnCount: number
  height: number
  placements: LibraryMasonryPlacement[]
}

/**
 * Сколько колонок влезает, как у repeat(auto-fill, minmax(min, 1fr)):
 * n * min + (n - 1) * gap <= inner. Узкий контейнер всё равно даёт одну колонку
 * на всю ширину — minmax в гриде в этом случае тоже сжимается.
 */
function columnCountFor(innerWidth: number, minColumnWidth: number, gap: number): number {
  if (!(minColumnWidth > 0)) return 1
  return Math.max(1, Math.floor((innerWidth + gap) / (minColumnWidth + gap)))
}

export function layoutLibraryMasonry({
  count,
  containerWidth,
  aspectRatio,
  minColumnWidth,
  gap = 12,
  padding = 12,
  captionPx = 28,
}: {
  count: number
  containerWidth: number
  aspectRatio: (index: number) => number
  minColumnWidth: number
  gap?: number
  padding?: number
  captionPx?: number
}): LibraryMasonryLayout {
  if (!Number.isFinite(containerWidth) || containerWidth <= 0 || count <= 0) {
    return { columnCount: 1, height: 0, placements: [] }
  }

  const innerWidth = Math.max(0, containerWidth - padding * 2)
  const columnCount = columnCountFor(innerWidth, minColumnWidth, gap)
  const columnWidth = columnCount <= 1 ? innerWidth : (innerWidth - gap * (columnCount - 1)) / columnCount
  const cursor = Array.from({ length: columnCount }, () => padding)
  const placements: LibraryMasonryPlacement[] = []

  for (let index = 0; index < count; index += 1) {
    const column = index % columnCount
    const height = libraryTileHeight(columnWidth, aspectRatio(index), captionPx)
    const top = cursor[column] ?? padding
    placements.push({
      index,
      column,
      top,
      left: padding + column * (columnWidth + gap),
      width: columnWidth,
      height,
    })
    cursor[column] = top + height + gap
  }

  const height = Math.max(...cursor) - gap + padding
  return { columnCount, height, placements }
}
